'use client';

import React, { useEffect, useState } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    GalleryHorizontalEnd,
    GalleryVerticalEnd
} from 'lucide-react';
import { getBook, incrementVisits } from "@/lib/firestore";
import Link from 'next/link';

type ReadingMode = 'cascade' | 'pagination';

interface ReaderProps {
    id: string;
    page?: string;
}

const MangaReader = ({ id, page = '1' }: ReaderProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [readingMode, setReadingMode] = useState<ReadingMode>('pagination');
    const [pages, setPages] = useState<string[]>([]);
    const [fitMode, setFitMode] = useState<'width' | 'height'>('height');
    const [isLoading, setIsLoading] = useState(true);  // Estado de carga

    useEffect(() => {
        const updateFitMode = () => {
            setFitMode(window.innerWidth <= 768 ? 'width' : 'height');
        };

        updateFitMode();
        window.addEventListener('resize', updateFitMode);

        return () => {
            window.removeEventListener('resize', updateFitMode);
        };
    }, []);

    const getFitClass = () => {
        return fitMode === 'width' ? 'w-full h-auto' : 'h-[calc(100vh-3.5rem)] w-auto';
    };

    useEffect(() => {
        const savedSettings = localStorage.getItem('readerSettings');
        if (savedSettings) {
            const { readingMode } = JSON.parse(savedSettings);
            setReadingMode(readingMode || 'pagination');
        }

        const initialPage = parseInt(page, 10);
        if (!isNaN(initialPage)) {
            setCurrentPage(initialPage);
        }
    }, [page]);

    useEffect(() => {
        const fetchBookDetails = async () => {
            try {
                await incrementVisits(id);
                const bookData = await getBook(id);
                setPages(bookData?.pages || []);
                setIsLoading(false);  // Una vez cargado, cambia el estado de carga
            } catch (error) {
                console.error("Error fetching book details:", error);
                setIsLoading(false);  // Si ocurre un error, también se detiene el cargador
            }
        };
        fetchBookDetails();
    }, [id]);

    useEffect(() => {
        if (readingMode === 'pagination') {
            window.history.replaceState(null, '', `/reader/${id}/${currentPage}`);
        } else {
            window.history.replaceState(null, '', `/reader/${id}/cascade`);
        }
    }, [currentPage, id, readingMode]);

    const goToPage = (newPage: number) => {
        if (newPage >= 1 && newPage <= pages.length) {
            setCurrentPage(newPage);
        }
    };

    const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
        if (readingMode === 'pagination') {
            const clickX = event.nativeEvent.offsetX;
            const imageWidth = event.currentTarget.width;
            if (clickX > imageWidth / 2) {
                goToPage(currentPage + 1);
            } else {
                goToPage(currentPage - 1);
            }
        }
    };

    const saveSettings = (updatedSettings: { readingMode?: ReadingMode }) => {
        const currentSettings = JSON.parse(localStorage.getItem('readerSettings') || '{}');
        const newSettings = { ...currentSettings, ...updatedSettings };
        localStorage.setItem('readerSettings', JSON.stringify(newSettings));
    };

    return (
        <div className="min-h-screen bg-zinc-900">
            {/* Navigation Bar */}
            <div className="fixed top-0 left-0 right-0 bg-zinc-800/95 backdrop-blur-sm border-b border-zinc-700 h-14 flex items-center justify-between px-4 z-50">
                <div className="flex items-center gap-2">
                    <Link
                        href={`/contents/${id}`}
                        className="text-zinc-400 hover:text-white transition-colors"
                    >
                        <ChevronLeft className="h-6 w-6" />
                    </Link>

                    {readingMode === 'pagination' && (
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => goToPage(1)}
                                className="p-2 text-zinc-400 hover:text-white transition-colors"
                                disabled={currentPage <= 1}
                            >
                                <ChevronsLeft className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => goToPage(currentPage - 1)}
                                className="p-2 text-zinc-400 hover:text-white transition-colors"
                                disabled={currentPage <= 1}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <span className="text-zinc-100 text-sm">
                                {currentPage}/{pages.length}
                            </span>
                            <button
                                onClick={() => goToPage(currentPage + 1)}
                                className="p-2 text-zinc-400 hover:text-white transition-colors"
                                disabled={currentPage >= pages.length}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => goToPage(pages.length)}
                                className="p-2 text-zinc-400 hover:text-white transition-colors"
                                disabled={currentPage >= pages.length}
                            >
                                <ChevronsRight className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>

                <button
                    onClick={() => {
                        const newMode = readingMode === 'pagination' ? 'cascade' : 'pagination';
                        setReadingMode(newMode);
                        saveSettings({ readingMode: newMode });
                    }}
                    className="text-zinc-400 hover:text-white transition-colors cursor-pointer"
                >
                    {readingMode === 'pagination' ? (
                        <GalleryVerticalEnd className="h-6 w-6" />
                    ) : (
                        <GalleryHorizontalEnd className="h-6 w-6" />
                    )}
                </button>
            </div>

            {/* Reader Content */}
            <div className="pt-14">
                {isLoading ? (
                    <div className="flex justify-center items-center h-[calc(100vh-3.5rem)]">
                        <div className="spinner-border animate-spin text-white h-12 w-12 border-4 border-t-transparent rounded-full" />
                    </div>
                ) : pages.length === 0 ? (  // Esto asegura que no se intente renderizar con páginas vacías
                    <div className="text-white text-center">No pages available</div>
                ) : readingMode === 'cascade' ? (
                    <div className="flex flex-col items-center">
                        {pages.map((pageSrc, index) => (
                            <img
                                key={index}
                                src={pageSrc}
                                alt={`Page ${index + 1}`}
                                className={getFitClass()}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="flex justify-center cursor-pointer items-center h-[calc(100vh-8.5rem)]">
                        <img
                            src={pages[currentPage - 1]}
                            alt={`Page ${currentPage}`}
                            className={getFitClass()}
                            onClick={handleImageClick}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MangaReader;
